import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';

import guarantorYellow from 'assets/images/guarantor-yellow.svg';
import InputBorderNone from 'components/Atoms/Inputs/InputBorderNone/InputBorderNone';
import { File, Loan, Task, TaskState } from 'gql/graphql';
import { useCreateResponseTask, useRemoveTaskResponse } from 'services/task/mutations';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import TagOwnerState from '../../Step2/CardOwnershipNew/TagOwnerState';
import { useCreateOwnership, useDeleteOwnership } from '../../../../../../../services/ownership/mutations';
import { LoanContext } from '../../../../Dashboard';
import ErrorBox from 'components/Atoms/Alerts/ErrorBox';
import alertIcon from 'assets/images/alert-triangle.svg';

export interface Ownership {
  _id?: string;
  key: string;
  name: string;
  lastname: string;
  ownership: number;
  dateBirth: string;
  email: string;
  phone: string;
  ssn: string;
  address: string;
  state: string;
  stakes: any;
  files: File[];
  taskResponse: any;
}

interface Props {
  index?: any;
  ownership: Ownership;
  ownerships?: Ownership[];
  isActionRemoved?: boolean;
  isOwnerModelEdit?: boolean;
  entity?: any;
  onDeleteOwner?: any;
  setNewPercentageLiveEdit?: any;
  handlerSwitchExpandCard?: any;
  disabledRemovedAction?: boolean;
  isReadOnly?: boolean;
}

let isUpdatePercentage = false;

const CardOwnership = ({
  ownership,
  ownerships,
  isActionRemoved,
  isOwnerModelEdit,
  entity,
  onDeleteOwner,
  index,
  setNewPercentageLiveEdit,
  handlerSwitchExpandCard,
  disabledRemovedAction,
  isReadOnly
}: Props) => {
  const [form] = Form.useForm();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loan: Loan | any = useContext(LoanContext);

  const { execute: fetchCreateOwnership } = useCreateOwnership();
  const { execute: createResponseTask } = useCreateResponseTask();
  const { data: responseRemove, fetching: loadingRemove, execute: removeTaskResponse } = useRemoveTaskResponse();
  const { execute: deleteOwner } = useDeleteOwnership();
  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: ownership.taskResponse.task._id } },
    pause: true
  });

  const [percentageOwner, setPercentageOwner] = useState<number>();
  const [showErrorBox, setShowErrorBox] = useState(false);
  const [isStep3, setStep3] = useState(false);

  useEffect(() => {
    getTaskResponses({
      requestPolicy: 'network-only',
      variables: { taskId: ownership.taskResponse.task._id }
    });
  }, [])

  useEffect(() => {
    const step3 = taskResponses?.find((item: any) => item.step === 3);
    setStep3(!!step3);
  }, [taskResponses]);

  useEffect(() => {
    if (responseRemove)
      getTaskResponses({
        requestPolicy: 'network-only',
        variables: { taskId: ownership.taskResponse.task._id }
      });
  }, [responseRemove]);

  useEffect(() => {
    if (ownership?.ownership) setPercentageOwner(ownership.ownership);
  }, [ownership]);

  useEffect(() => {
    if (!isUpdatePercentage) form.resetFields()
  }, [ownership]);

  const changeEdit = (newState?: 'close') => {
    if (handlerSwitchExpandCard)
      handlerSwitchExpandCard(ownership._id || (ownership as any).ownerId, newState === 'close');
  };

  const changeOwnership = async (_ownership: any) => {
    isUpdatePercentage = true;

    const {
      files: ignoreFile,
      task,
      __typename,
      createdAt,
      ...taskResponse
    } = ownership.taskResponse;

    let isCompleteProfile = false;

    if (_ownership && entity &&
      taskResponse.metadata.address &&
      taskResponse.metadata.dateBirth &&
      taskResponse.metadata.email &&
      taskResponse.metadata.phone &&
      taskResponse.metadata.ssn &&
      ignoreFile?.length > 0
    ) {
      isCompleteProfile = true;
      const { ownerId, referenceId, ...metadata } = taskResponse.metadata;

      await fetchCreateOwnership({
        ...metadata,
        stakes: { ...ownership.stakes, [entity._id]: _ownership },
        entity: entity._id,
        loan: loan._id,
        _id: ownerId
      });
    }

    let newState = _ownership < 20 ? TaskState.Draft :
      isCompleteProfile && !taskResponse?.metadata?._id ? TaskState.Draft : TaskState.Incomplete;

    if (taskResponse.state === TaskState.RequestError && !isValidForm(_ownership, null, true)) {
      newState = TaskState.RequestError;
    }

    await createResponseTask({
      ...taskResponse,
      task: task._id,
      state: newState,
      metadata: { ...taskResponse.metadata, ownership: _ownership }
    });

    setNewPercentageLiveEdit(null);
  };

  const deleteOwnership = async () => {
    removeTaskResponse({ taskResponseId: ownership.key });

    const ownerId = ownership?._id || (ownership as any)?.ownerId;

    if (isOwnerModelEdit && ownerId)
      deleteOwner({ ownerId, entityId: entity._id, loanId: loan._id });

    if (onDeleteOwner)
      onDeleteOwner(ownerId);
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isValidForm = (values: any, files: any, ignoreOwnershipData?: boolean, ignoreFiles?: boolean) => {
    return (values.address || (!ignoreOwnershipData && ownership.address)) &&
      (values.dateBirth || (!ignoreOwnershipData && ownership.dateBirth)) &&
      (values.email || (!ignoreOwnershipData && ownership.email)) &&
      (values.phone || (!ignoreOwnershipData && ownership.phone)) &&
      (values.ssn || (!ignoreOwnershipData && ownership.ssn)) &&
      (ignoreFiles || files?.length > 0 || (!ignoreOwnershipData && ownership?.files?.length > 0)) &&
      (values.ownership || ownership.ownership);
  }

  const getEffectiveState = (): TaskState => (
    isReadOnly ? TaskState.Approved : (ownership.state as TaskState)
  );

  const effectiveState = getEffectiveState();

  type TagOwnerStatus = 'no-guarantor' | 'error' | 'next-step' | 'incomplete' | 'verify' | 'complete';

  const tagOwnerState: TagOwnerStatus = (() => {
    const rules: Array<{ match: boolean; value: TagOwnerStatus }> = [
      {
        match: !ownership.ownership || ownership.ownership < 20,
        value: 'no-guarantor',
      },
      {
        match: effectiveState === TaskState.RequestError,
        value: 'error',
      },
      {
        match:
          (!ownership._id || !isValidForm(ownership, ownership.files)) &&
          effectiveState === TaskState.Incomplete &&
          !isReadOnly,
        value: 'next-step',
      },
      {
        match: !ownership._id && effectiveState === TaskState.Incomplete,
        value: 'incomplete',
      },
      {
        match:
          Boolean(ownership._id) &&
          effectiveState === TaskState.Incomplete &&
          isValidForm(ownership, ownership.files) &&
          isReadOnly,
        value: 'verify',
      },
      {
        match:
          Boolean(ownership._id) &&
          effectiveState === TaskState.Incomplete &&
          !isValidForm(ownership, ownership.files),
        value: 'incomplete',
      },
    ];

    return rules.find(rule => rule.match)?.value ?? 'complete';
  })();

  // Deshabilitar eliminar si:
  // 1. Está cargando la eliminación
  // 2. Es el primer ownership y es el único que no está completo (debe quedar al menos uno completo)
  // 3. Es el primer ownership y es el único (debe quedar al menos uno)
  const isFirstOwnership = index === 0;
  const completedOwnerships = ownerships?.filter(item => item.state !== TaskState.Incomplete) ?? [];
  const hasOnlyOneComplete = completedOwnerships.length === 1;
  const hasOnlyOneTotal = ownerships?.length === 1;

  const disabledRemove = loadingRemove ||
    (isFirstOwnership && (hasOnlyOneComplete || hasOnlyOneTotal)) ||
    disabledRemovedAction;

  useEffect(() => {
    if (!disabledRemove || !(ownerships?.length === 1)) setShowErrorBox(false);
  }, [disabledRemove, ownerships])

  const renderCardResume = () => {
    return (
      <div className={`card-ownership`}>
        <div className="section-1">
          <div className="resume-names" style={{ marginTop: 5 }}>
            <StateTag
              state={effectiveState}
              isOtherState={effectiveState === TaskState.Draft}
              isLabelLocked
              colorState={effectiveState === TaskState.Draft ? TaskState.Draft : effectiveState}
              isDraftState={effectiveState === TaskState.Draft}
            />
            <div className="name">
              <span>{ownership.name + ' ' + ownership.lastname}</span>
            </div>
          </div>
          <TagOwnerState
            className="tag-owner-state"
            onChange={changeEdit}
            state={tagOwnerState}
            disabled={effectiveState === TaskState.Approved}
            disabledExpand={!isStep3}
          />
        </div>
        <div className="section-2">
          <div className='ownership-section'>
            {percentageOwner && percentageOwner >= 20 &&
              <span className="body-regular body-regular-w5 guarantor">
                <img src={guarantorYellow} className="lienzo" alt="lienzoSvg" />{' '}
                Guarantor
              </span>
            }
            <InputBorderNone
              type="number"
              placeholder="Ownership"
              suffix={percentageOwner ? '%' : ' '}
              className={`input-disabled-color ${!percentageOwner && 'suffix-null'} mt-0`}
              disabled={
                effectiveState !== TaskState.Incomplete &&
                effectiveState !== TaskState.RequestError &&
                effectiveState !== TaskState.Draft
              }
              color="warning"
              width={percentageOwner ? '50px' : '80px'}
              max={100}
              min={1}
              value={percentageOwner}
              onChange={(value: number) => {
                setPercentageOwner(value);
                setNewPercentageLiveEdit({ ownerId: (ownership._id || (ownership as any).ownerId), value });
              }}
              onBlur={(e: any) => {
                if (e.target.value > 0) changeOwnership(parseInt(e.target.value));
              }}
              deletefull={true}
            />
          </div>
        </div>
      </div>
    );
  }

  const renderCardEdit = () => {
    return (
      <div className={`card-ownership`}>
        <div className="section-1">
          {effectiveState === TaskState.Approved &&
            <div className="resume-names">
              <StateTag state={TaskState.Approved} colorState={TaskState.Approved}/>
            </div>
          }
          <div className="names" style={{ marginTop: 0 }}>
            <div className="name">
              <label className="body-regular text-blue">First name</label>
              <span>{ownership.name}</span>
            </div>
            {ownership.lastname && (
              <div className="name">
                <label className="body-regular text-blue">Last name</label>
                <span>{ownership.lastname}</span>
              </div>
            )}
          </div>
          <TagOwnerState
            className="tag-owner-state"
            onChange={changeEdit}
            state={isReadOnly ? 'complete' : tagOwnerState}
            disabled={effectiveState === TaskState.Approved}
            disabledExpand={true}
          />
        </div>
        <div className="section-2">
          <div className='ownership-section'>
            {percentageOwner && percentageOwner >= 20 &&
              <span className="body-regular body-regular-w5 guarantor">
                <img src={guarantorYellow} className="lienzo" alt="lienzoSvg" />{' '}
                Guarantor
              </span>
            }
            <InputBorderNone
              type="number"
              placeholder="Ownership"
              suffix={percentageOwner ? '%' : ' '}
              className={`input-disabled-color ${!percentageOwner && 'suffix-null'} mt-0`}
              disabled={
                effectiveState !== TaskState.Incomplete &&
                effectiveState !== TaskState.RequestError &&
                effectiveState !== TaskState.Draft
              }
              color="warning"
              width={percentageOwner ? '50px' : '80px'}
              max={100}
              min={1}
              value={percentageOwner}
              onChange={(value: number) => {
                setPercentageOwner(value);
                setNewPercentageLiveEdit({ ownerId: (ownership._id || (ownership as any).ownerId), value });
              }}
              onBlur={(e: any) => {
                if (e.target.value > 0) changeOwnership(parseInt(e.target.value));
              }}
              deletefull={true}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }} id={ownership.key} className={`card-ownership`}>
      {isActionRemoved && (
        <>
          <CloseCircleFilled
            className={`icon-delete ${(disabledRemove) && 'icon-delete-disabled'}`}
            onClick={!disabledRemove ? deleteOwnership : () => { }}
            disabled={disabledRemove || isReadOnly}
          />
          {disabledRemove && !isReadOnly && (
            <div
              onClick={() => setShowErrorBox(true)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                cursor: 'not-allowed',
                backgroundColor: 'transparent'
              }}
            />
          )}
          {showErrorBox && disabledRemove && (
            <ErrorBox text='You must have at least one guarantor on this loan' img={alertIcon} className="mb-10" />
          )}
        </>
      )}
      <div
        className={`content-card ${isReadOnly ? 'content-card-' + effectiveState : ''
          } ${!isActionRemoved ? 'content-card-hover-removed' : ''}`}
        style={{
          height: isReadOnly ? 74 : 'auto',
          padding: isReadOnly ? '5px 18px 5px 16px' : '10px 18px 5px 16px'
        }}
      >
        {!isReadOnly ? renderCardEdit() : renderCardResume()}
      </div>
    </div>
  );
};
export default CardOwnership;
